import { TrashIcon, DotsVerticalIcon } from "@radix-ui/react-icons";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";

import { AddFeedButton } from "@/components/AddFeedButton";
import {
  AddStickyPostToCampaignButton,
  IsProgrammaticContext,
} from "@/components/AddStickyPostToCampaignButton";
import { ErrorBanner } from "@/components/Callout";
import { CampaignPostPreview } from "@/components/CampaignPostPreview";
import { CreditCardDialog } from "@/components/CreditCardDialog";
import { MoneyInput } from "@/components/MoneyInput";
import { Page } from "@/components/Page";
import { StripeWrapper } from "@/components/StripeWrapper";
import { FeedAvatar } from "@/components/ui/avatar";
import { Button, ButtonLink } from "@/components/ui/button";
import { Input, NumberInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@/components/ui/menu";
import { SimpleSelect } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { H1, P } from "@/components/ui/typography";
import {
  addPostToCampaign,
  createCampaign,
  getAccountDetailsQueryOptions,
  getCurrentUserQueryOptions,
  getMonetizedFeeds,
  proposeCampaign,
} from "@/lib/api";
import { Post, PublicFeed } from "@/lib/types";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/_sidebar-layout/campaigns/create")({
  component: HomeComponent,
  validateSearch: (search) => {
    return {
      feeds: search.feeds as string[],
    };
  },
  loader: ({ context: { queryClient } }) => {
    void Promise.all([
      queryClient.prefetchQuery(getAccountDetailsQueryOptions()),
      queryClient.prefetchQuery(getCurrentUserQueryOptions()),
    ]);
  },
  head: () => ({ meta: [{ title: "Create Campaigns" }] }),
});

function NameInput({
  value,
  setValue,
}: {
  value: string;
  setValue: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <Label htmlFor="name">Name</Label>
      <Input
        required
        value={value}
        onValueChange={(v) => setValue(v)}
        name="name"
        placeholder="Campaign Name"
        id="campaign-name"
        autoComplete="off"
      />
    </div>
  );
}

function BudgetCell({
  impressions_budget,
  cpm_cents,
  discount_percentage,
}: {
  impressions_budget: number;
  cpm_cents: number;
  discount_percentage: number | undefined;
}) {
  const budget = ((impressions_budget || 0) * (cpm_cents / 100)) / 1000;

  return discount_percentage ? (
    <div>${(budget - (budget * discount_percentage) / 100).toFixed(2)}</div>
  ) : (
    <div>${budget.toFixed(2)}</div>
  );
}

function StickyPostsTable({
  stickyPosts,
  setStickyPosts,
  urlsWithErrors,
}: {
  stickyPosts: Array<{ post_url: string; post: Post }>;
  setStickyPosts: React.Dispatch<
    React.SetStateAction<Array<{ post_url: string; post: Post }>>
  >;
  urlsWithErrors: Record<string, string>;
}) {
  const onAddStickyPost = (data: { post_url: string; post: Post }) => {
    setStickyPosts((prev) => [...prev, data]);
  };
  const onRemoveStickyPost = (data: { post_url: string }) => {
    setStickyPosts((prev) =>
      prev.filter((stickyPost) => stickyPost.post_url !== data.post_url),
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="text-lg font-medium">Advertised Posts</div>
        <AddStickyPostToCampaignButton onAddStickyPost={onAddStickyPost} />
      </div>
      {stickyPosts.length > 0 ? (
        <div className="rounded-lg overflow-hidden border border-slate-6 dark:border-slatedark-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Post</TableHead>
                <TableHead className="flex items-center justify-end">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stickyPosts.map((stickyPost) => {
                return (
                  <TableRow
                    key={`${stickyPost.post_url}`}
                    className={cn(
                      "group cursor-pointer",
                      urlsWithErrors[stickyPost.post_url] &&
                        "bg-reda-3 hover:bg-reda-4 dark:bg-reddarka-3 dark:hover:bg-reddarka-4",
                    )}
                  >
                    <TableCell>
                      <CampaignPostPreview uri={stickyPost.post_url} />
                    </TableCell>
                    <TableCell>
                      <div className="w-full flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="smIcon"
                          onPress={() => onRemoveStickyPost(stickyPost)}
                        >
                          <span className="sr-only">Remove post</span>
                          <TrashIcon />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-sm text-slate-dim">
          You have not added any posts yet.
        </div>
      )}
    </div>
  );
}

function ManualCampaignForm() {
  const [name, setName] = useState("");
  const [stickyPosts, setStickyPosts] = useState<
    Array<{ post_url: string; post: Post }>
  >([]);
  const [urlsWithErrors, setUrlsWithErrors] = useState<Record<string, string>>(
    {},
  );
  const navigate = useNavigate({ from: "/app/campaigns/create" });
  const [campaigns, setCampaigns] = useState<
    Array<{
      feed: PublicFeed;
      cpm_cents: number;
      impressions_budget: number;
      reason: string | undefined;
      duration: number | undefined;
      coupon: string | undefined;
      discount_percentage: number | undefined;
    }>
  >([]);
  const hasVideoFeeds = campaigns.some(
    (campaign) => campaign.feed.is_video_feed,
  );
  const stickyPostsOnlyHaveVideoPosts = stickyPosts.every((stickyPost) =>
    stickyPost.post.embed?.$type.includes("video"),
  );
  const cantCreateVideoCampaign =
    hasVideoFeeds && !stickyPostsOnlyHaveVideoPosts;

  const { feeds = [] } = Route.useSearch();
  useEffect(() => {
    Promise.all(
      feeds.map(async (feed) => {
        const { results } = await getMonetizedFeeds({
          algo_id: parseInt(feed),
          days: 30,
        });
        const feedDef = results[0];

        if (feedDef) {
          setCampaigns((prev) => [
            ...prev,
            {
              cpm_cents: feedDef.algorithm.cpm_cents ?? 100,
              feed: feedDef.algorithm,
              impressions_budget: 10000,
              reason: undefined,
              duration: undefined,
              coupon: undefined,
              discount_percentage: undefined,
            },
          ]);
        }
      }),
    ).catch((error) => {
      console.error(error);
    });
  }, [feeds]);

  const createCampaignMutation = useMutation({
    onSuccess: () => navigate({ to: "/app/campaigns" }),
    onError: (error: AxiosError) => console.error(error),
    mutationFn: async () => {
      setUrlsWithErrors({});

      if (!stickyPosts.length) {
        throw new Error("At least one post is required.");
      }

      if (!campaigns.length) {
        throw new Error("At least one feed is required.");
      }

      const campaign = await createCampaign({
        display_name: name,
        type: "manual",
      });

      const newUrlsWithErrors: Record<string, string> = {};

      await Promise.all(
        stickyPosts.map(async (stickyPost) => {
          const response = await addPostToCampaign(campaign.id, {
            post_url: stickyPost.post_url,
            is_active: true,
          });

          if ("error" in response) {
            newUrlsWithErrors[stickyPost.post_url] = response.error;
          }
        }),
      );

      if (Object.keys(newUrlsWithErrors).length) {
        setUrlsWithErrors(newUrlsWithErrors);
        throw new Error("All posts must contain an ad or sponsored hashtag.");
      }

      await Promise.all(
        campaigns.map((algo) =>
          proposeCampaign(campaign.id, algo.feed.id, algo),
        ),
      );
    },
  });

  return (
    <form
      className="contents"
      onSubmit={(e) => {
        e.preventDefault();
        createCampaignMutation.mutate();
      }}
    >
      <NameInput value={name} setValue={setName} />
      <StickyPostsTable
        stickyPosts={stickyPosts}
        setStickyPosts={setStickyPosts}
        urlsWithErrors={urlsWithErrors}
      />
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="text-lg font-medium">Feeds</div>
          <AddFeedButton
            // eslint-disable-next-line @typescript-eslint/require-await
            onAddCampaign={async (data) => {
              setCampaigns((prev) => [...prev, data]);
            }}
          />
        </div>

        <div className="md:hidden rounded-lg overflow-hidden">
          {campaigns.length > 0 ? (
            campaigns
              .reverse()
              .map(
                ({
                  feed,
                  cpm_cents,
                  impressions_budget,
                  duration,
                  discount_percentage,
                }) => {
                  return (
                    <div
                      key={`${feed.id}-campaign`}
                      className="p-4 border border-slate-6 dark:border-slatedark-6 rounded-lg flex flex-col gap-4"
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FeedAvatar feed={feed} />
                          <div>
                            <div className="text-lg font-medium truncate">
                              {feed.display_name}
                            </div>
                          </div>
                        </div>
                        <DropdownMenu>
                          <MenuTrigger asChild>
                            <Button variant="ghost" size="smIcon">
                              <span className="sr-only">Actions</span>
                              <DotsVerticalIcon />
                            </Button>
                          </MenuTrigger>
                          <MenuContent align="end">
                            <MenuItem
                              onSelect={() => {
                                setCampaigns((prev) =>
                                  prev.filter(
                                    (campaign) => campaign.feed.id !== feed.id,
                                  ),
                                );
                              }}
                            >
                              Delete
                            </MenuItem>
                          </MenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="duration">Duration</Label>
                        <NumberInput
                          id="duration"
                          value={duration}
                          min={1}
                          max={100}
                          onValueChange={(v) =>
                            setCampaigns((prev) =>
                              prev.map((campaign) =>
                                campaign.feed.id === feed.id
                                  ? {
                                      ...campaign,
                                      duration: v,
                                    }
                                  : campaign,
                              ),
                            )
                          }
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="cpm">CPM</Label>
                        <MoneyInput
                          id="cpm"
                          value={cpm_cents / 100}
                          min={(feed.cpm_cents ?? 100) / 100}
                          onValueChange={(v) =>
                            setCampaigns((prev) =>
                              prev.map((campaign) =>
                                campaign.feed.id === feed.id
                                  ? {
                                      ...campaign,
                                      cpm_cents: v * 100,
                                    }
                                  : campaign,
                              ),
                            )
                          }
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <Label htmlFor="impressions">Impressions</Label>
                        <NumberInput
                          id="impressions"
                          min={0}
                          value={impressions_budget}
                          onValueChange={(v) =>
                            setCampaigns((prev) =>
                              prev.map((campaign) =>
                                campaign.feed.id === feed.id
                                  ? {
                                      ...campaign,
                                      impressions_budget: v,
                                    }
                                  : campaign,
                              ),
                            )
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between p-2">
                        <div className="text-sm text-slate-dim">Budget</div>
                        <div className="font-bold">
                          <BudgetCell
                            impressions_budget={impressions_budget}
                            cpm_cents={cpm_cents}
                            discount_percentage={discount_percentage}
                          />
                        </div>
                      </div>
                    </div>
                  );
                },
              )
          ) : (
            <div className="text-sm text-slate-dim">
              You have not added any feeds yet.
            </div>
          )}
        </div>

        <div className="hidden md:block">
          {campaigns.length > 0 ? (
            <div className="rounded-lg overflow-hidden border border-slate-6 dark:border-slatedark-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Feed</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>CPM</TableHead>
                    <TableHead>Impressions</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead className="flex items-center justify-end">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns
                    .reverse()
                    .map(
                      ({
                        feed,
                        cpm_cents,
                        impressions_budget,
                        duration,
                        discount_percentage,
                      }) => {
                        return (
                          <TableRow
                            key={`${feed.id}-campaign`}
                            className="group cursor-pointer"
                          >
                            <TableCell>{feed.display_name}</TableCell>
                            <TableCell>
                              <NumberInput
                                value={duration}
                                min={1}
                                max={100}
                                onValueChange={(v) =>
                                  setCampaigns((prev) =>
                                    prev.map((campaign) =>
                                      campaign.feed.id === feed.id
                                        ? {
                                            ...campaign,
                                            duration: v,
                                          }
                                        : campaign,
                                    ),
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <MoneyInput
                                value={cpm_cents / 100}
                                min={(feed.cpm_cents ?? 100) / 100}
                                onValueChange={(v) =>
                                  setCampaigns((prev) =>
                                    prev.map((campaign) =>
                                      campaign.feed.id === feed.id
                                        ? {
                                            ...campaign,
                                            cpm_cents: v * 100,
                                          }
                                        : campaign,
                                    ),
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <NumberInput
                                min={0}
                                value={impressions_budget}
                                onValueChange={(v) =>
                                  setCampaigns((prev) =>
                                    prev.map((campaign) =>
                                      campaign.feed.id === feed.id
                                        ? {
                                            ...campaign,
                                            impressions_budget: v,
                                          }
                                        : campaign,
                                    ),
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <BudgetCell
                                impressions_budget={impressions_budget}
                                cpm_cents={cpm_cents}
                                discount_percentage={discount_percentage}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="w-full flex justify-end">
                                <DropdownMenu>
                                  <MenuTrigger asChild>
                                    <Button variant="ghost" size="smIcon">
                                      <span className="sr-only">Actions</span>
                                      <DotsVerticalIcon />
                                    </Button>
                                  </MenuTrigger>
                                  <MenuContent align="end">
                                    <MenuItem
                                      onSelect={() => {
                                        setCampaigns((prev) =>
                                          prev.filter(
                                            (campaign) =>
                                              campaign.feed.id !== feed.id,
                                          ),
                                        );
                                      }}
                                    >
                                      Delete
                                    </MenuItem>
                                  </MenuContent>
                                </DropdownMenu>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      },
                    )}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-sm text-slate-dim">
              You have not added any feeds yet.
            </div>
          )}
        </div>
      </div>
      {createCampaignMutation.error && (
        <ErrorBanner error={createCampaignMutation.error} />
      )}
      <div className="flex justify-between gap-4 mt-6">
        <div className="flex-1" />
        <div className="flex gap-2">
          <ButtonLink size="lg" variant="outline" to="/app/campaigns">
            Cancel
          </ButtonLink>
          <Button
            size="lg"
            isLoading={createCampaignMutation.isPending}
            type="submit"
            disabled={cantCreateVideoCampaign}
          >
            Create
          </Button>
        </div>
      </div>
    </form>
  );
}

function ProgrammaticCampaignForm() {
  const [name, setName] = useState("");
  const [cpmInflight, setCpmInflight] = useState<number>(0);
  const [stickyPosts, setStickyPosts] = useState<
    Array<{ post_url: string; post: Post }>
  >([]);
  const navigate = useNavigate({ from: "/app/campaigns/create" });
  const createCampaignMutation = useMutation({
    onSuccess: () => navigate({ to: "/app/campaigns" }),
    onError: (error: AxiosError) => console.error(error),
    mutationFn: async () => {
      if (!stickyPosts.length) {
        throw new Error("At least one post is required.");
      }

      await createCampaign({
        display_name: name,
        cpm_inflight: cpmInflight,
        type: "auto",
        sticky_post_uris: stickyPosts.map((stickyPost) => stickyPost.post_url),
      });
    },
  });

  return (
    <form
      className="contents"
      onSubmit={(e) => {
        e.preventDefault();
        createCampaignMutation.mutate();
      }}
    >
      <NameInput value={name} setValue={setName} />
      <div className="flex flex-col gap-3">
        <Label htmlFor="cpm-inflight">CPM</Label>
        <MoneyInput
          id="cpm-inflight"
          value={cpmInflight}
          onValueChange={setCpmInflight}
          min={0}
          placeholder="0.00"
          required
        />
        <P className="text-xs text-slate-dim">
          Cost per thousand impressions for this campaign.
        </P>
      </div>
      <IsProgrammaticContext value={true}>
        <StickyPostsTable
          stickyPosts={stickyPosts}
          setStickyPosts={setStickyPosts}
          urlsWithErrors={{}}
        />
      </IsProgrammaticContext>

      {createCampaignMutation.error && (
        <ErrorBanner error={createCampaignMutation.error} />
      )}
      <div className="flex justify-between gap-4 mt-6">
        <div className="flex-1" />
        <div className="flex gap-2">
          <ButtonLink size="lg" variant="outline" to="/app/campaigns">
            Cancel
          </ButtonLink>
          <Button
            size="lg"
            isLoading={createCampaignMutation.isPending}
            type="submit"
          >
            Create
          </Button>
        </div>
      </div>
    </form>
  );
}

function CreateCampaignForm() {
  const [type, setType] = useState<"programmatic" | "manual">("manual");
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());

  return (
    <div className="flex flex-col gap-4 md:gap-8">
      {user.feature_flags?.includes("programmatic-ads") && (
        <div className="flex flex-col gap-3">
          <Label htmlFor="name">Type</Label>
          <SimpleSelect
            aria-label="Type of campaign"
            options={[
              { label: "Auto", value: "programmatic" },
              { label: "Manual", value: "manual" },
            ]}
            value={type}
            onValueChange={(v) => setType(v as "programmatic" | "manual")}
          />
        </div>
      )}
      {type === "manual" && <ManualCampaignForm />}
      {type === "programmatic" && <ProgrammaticCampaignForm />}
    </div>
  );
}

function HomeComponent() {
  const { data: accountDetails } = useSuspenseQuery(
    getAccountDetailsQueryOptions(),
  );

  return (
    <Page className="md:gap-10">
      {accountDetails.can_spend_on_campaigns ? (
        <>
          <H1>Create Campaign</H1>
          <CreateCampaignForm />
        </>
      ) : (
        <div className="max-w-[600px] w-full mx-auto flex flex-col gap-8 bg-slate-subtle p-8 border border-slate-7 dark:border-slatedark-7 rounded-lg">
          <H1>Credit Card Required</H1>
          <div className="flex flex-col gap-4">
            <P>You must have a credit card on file to create campaigns.</P>
            <StripeWrapper>
              <CreditCardDialog />
            </StripeWrapper>
          </div>
        </div>
      )}
    </Page>
  );
}
